// Charter booking details: header, the cards, the passenger manifest, invoices, revenue and the edit modals.
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App as AntdApp, Button, Form, Popconfirm, Space, Spin, Tag, Tooltip, Typography } from 'antd';
import { ArrowLeftOutlined, EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { clockName, fmtZoned, instantToWall, wallToInstant, zoneForCode } from '../../data/airportTime';
import type { AirportHit } from '../../api/types';

import {
  getCharterBooking,
  listAircraft,
  listOperators,
  updateCharterBooking,
  cancelCharterBooking,
} from '../../api/endpoints';
import { CHARTER_BOOKING_STATUS_COLORS, CHARTER_BOOKING_STATUS_LABELS, statusLabel } from '../../data/statuses';
import { CharterInvoicesCard } from '../../components/CharterInvoicesCard';
import { useSingleSubmit } from '../../hooks/useSingleSubmit';
import { FlightDetailsCard } from './FlightDetailsCard';
import { ItineraryCard } from './ItineraryCard';
import { AircraftDetailsCard } from './AircraftDetailsCard';
import { FboCard } from './FboCard';
import { PassengersCard } from './PassengersCard';
import { RevenueCard } from './RevenueCard';
import { FlightDetailsModal } from './FlightDetailsModal';
import { FboModal } from './FboModal';
import { ItineraryModal } from './ItineraryModal';
import { AircraftModal } from './AircraftModal';

const { Title, Text } = Typography;

/**
 * SHAR-120/122/123 — one charter booking: flight information, the passenger
 * manifest and the revenue behind it.
 *
 * Booking ID, Request ID and the customer are shown but never editable, as the
 * AC requires: they identify the booking rather than describe it, and an admin
 * who could retype them could quietly point a booking at someone else.
 */
export function CharterBookingDetailPage() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  // SHAR-1256 — back to the list as it was left (its filters are in its address)
  const backTo = (useLocation().state as { from?: string } | null)?.from ?? '/charter-bookings';
  const qc = useQueryClient();
  const { message, modal } = AntdApp.useApp();
  const [flightForm] = Form.useForm();
  const [fboForm] = Form.useForm();
  const [itineraryForm] = Form.useForm();
  const [flightOpen, setFlightOpen] = useState(false);
  const [fboOpen, setFboOpen] = useState(false);
  // SHAR-738 — the aircraft-details editor. Operator first, then that operator's
  // aircraft, with the category following the aircraft: the same three controls
  // the ShareJet booking uses, rather than five free-text boxes in which an
  // admin could type a model no aeroplane has.
  const [aircraftOpen, setAircraftOpen] = useState(false);
  const [operatorId, setOperatorId] = useState<string | undefined>(undefined);
  const [aircraftId, setAircraftId] = useState<string | undefined>(undefined);
  // SHAR-735 — the itinerary editor.
  const [itineraryOpen, setItineraryOpen] = useState(false);
  // SHAR-1192 — the origin picked in the itinerary editor (its zone reads the departure).
  const [itineraryOriginAp, setItineraryOriginAp] = useState<AirportHit | null>(null);

  const q = useQuery({
    queryKey: ['charter-booking', id],
    queryFn: () => getCharterBooking(id),
    enabled: !!id,
  });
  const b = q.data;

  const saveMut = useMutation({
    mutationFn: (body: Parameters<typeof updateCharterBooking>[1]) =>
      updateCharterBooking(id, body),
    onSuccess: (fresh) => {
      qc.setQueryData(['charter-booking', id], fresh);
      qc.invalidateQueries({ queryKey: ['charter-bookings'] });
      setFlightOpen(false);
      setFboOpen(false);
      setAircraftOpen(false);
      setItineraryOpen(false);
      message.success('Saved');
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Update failed'),
  });

  // SHAR-1028 - removing the "Wire transfer required" ad-hoc label. Its own mutation:
  // saveMut closes the edit modals and says "Saved", neither of which fits.
  // SHAR-1027 - the same mutation removes "Passport needed".
  const dismissMut = useMutation({
    mutationFn: (label: 'transfer' | 'passport') =>
      updateCharterBooking(
        id,
        label === 'transfer' ? { dismissAwaitingTransfer: true } : { dismissPassportNeeded: true },
      ),
    onSuccess: (fresh, label) => {
      qc.setQueryData(['charter-booking', id], fresh);
      qc.invalidateQueries({ queryKey: ['charter-bookings'] });
      message.success(label === 'transfer' ? 'Removed “Wire transfer required”' : 'Removed “Passport needed”');
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Update failed'),
  });

  useEffect(() => {
    if (flightOpen && b) {
      // SHAR-792 — the STORED status, not the displayed one. `status` may read
      // 'complete' for a confirmed booking that has already landed, and that is
      // a derived value with no option in this Select: setting it would blank
      // the field and a save would then post a status the API does not accept.
      flightForm.setFieldsValue({ status: b.rawStatus ?? b.status, flightNumber: b.flightNumber ?? '' });
    }
  }, [flightOpen, b, flightForm]);

  useEffect(() => {
    if (fboOpen && b) {
      fboForm.setFieldsValue({
        depName: b.fbo?.departure?.name ?? '',
        depAddress: b.fbo?.departure?.address ?? '',
        depPhone: b.fbo?.departure?.phone ?? '',
        arrName: b.fbo?.arrival?.name ?? '',
        arrAddress: b.fbo?.arrival?.address ?? '',
        arrPhone: b.fbo?.arrival?.phone ?? '',
      });
    }
  }, [fboOpen, b, fboForm]);

  // SHAR-735 — the itinerary form opens on what the booking currently shows,
  // which is the quote's route and departure until somebody corrects them.
  useEffect(() => {
    if (itineraryOpen && b) {
      itineraryForm.setFieldsValue({
        originCode: b.originCode,
        destCode: b.destCode,
        // SHAR-1192 — the pickers show the origin airport's clock, not the browser's.
        departureDate: instantToWall(b.departureDateTime, b.departureZoned?.timezone),
        departureTime: instantToWall(b.departureDateTime, b.departureZoned?.timezone),
      });
    }
  }, [itineraryOpen, b, itineraryForm]);

  // SHAR-738 — the dropdowns open on nothing chosen: the booking already shows
  // its aircraft, and an operator pre-selected from a NAME rather than an id
  // would be a guess. Choosing an operator is the first deliberate act.
  useEffect(() => {
    if (aircraftOpen) {
      setOperatorId(undefined);
      setAircraftId(undefined);
    }
  }, [aircraftOpen]);

  // SHAR-124 — cancel the booking; the request goes with it, server-side.
  //
  // Declared with the other hooks, ABOVE the loading early-return: a hook after
  // it runs on some renders and not others, which React rejects (#310) — and
  // the page it broke was this one, so the Cancel button never appeared at all.
  const cancelMut = useMutation({
    mutationFn: () => cancelCharterBooking(id),
    onSuccess: (res) => {
      message.success(
        res?.alreadyCancelled
          ? 'This booking was already cancelled'
          : 'Charter booking cancelled; the customer has been told',
      );
      qc.invalidateQueries({ queryKey: ['charter-booking', id] });
      qc.invalidateQueries({ queryKey: ['charter-bookings'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Cancel failed'),
  });

  const operatorsQ = useQuery({
    queryKey: ['operators', 'active-all'],
    queryFn: () => listOperators({ pageSize: 200, status: 'active' }),
    enabled: aircraftOpen,
  });
  const aircraftQ = useQuery({
    queryKey: ['aircraft', 'by-operator', operatorId],
    // The aircraft endpoint caps pageSize at 100 and answers 400 above it, so a
    // 200 here made every model list empty. 21 aircraft exist in total.
    queryFn: () => listAircraft({ operatorId, status: 'active', pageSize: 100 }),
    enabled: aircraftOpen && !!operatorId,
  });
  const chosenAircraft = (aircraftQ.data?.items ?? []).find((a) => a.id === aircraftId);

  // Regression 28 Sep — one submit at a time on this page's forms.
  const submitGuard = useSingleSubmit();
  // Regression 28 Sep — an unknown id left the page blank (a spinner with
  // nothing behind it once the request had failed). Say so, as bookings does.
  if (q.isLoading || !b) {
    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => nav(backTo)}>
          Back to charter bookings
        </Button>
        <div style={{ textAlign: 'center', padding: 48 }}>
          {q.isLoading ? <Spin /> : <Text type="secondary">Charter booking not found.</Text>}
        </div>
      </Space>
    );
  }

  // SHAR-773 — a cancelled charter booking is final, so its editors are disabled
  // rather than left inviting a change the server now refuses. The tooltip says
  // why; a greyed-out button with no explanation just looks broken.
  const cancelled = (b?.rawStatus ?? b?.status) === 'cancelled';
  const editBtn = (onClick: () => void) => (
    <Tooltip title={cancelled ? 'This booking is cancelled — its details can no longer be changed' : ''}>
      <Button icon={<EditOutlined />} disabled={cancelled} onClick={onClick}>
        Edit
      </Button>
    </Tooltip>
  );

  const submitFlight = submitGuard.wrap(async () => {
    const v = await flightForm.validateFields();
    // Cancelling a booking is not a status like the others: it ends the
    // arrangement, so it is confirmed separately rather than saved silently
    // along with a flight number.
    // SHAR-773 — the same warning a ShareJet booking gives (SHAR-591), because
    // it is now the same rule: cancelling a charter booking is terminal. The
    // admin has to be told BEFORE the click that there is no route back —
    // afterwards the editors are disabled and there is nothing left to discover
    // the rule from. The refund note stays: it is the one thing that is true of
    // a charter cancellation and not of a ShareJet one.
    if (v.status === 'cancelled' && (b.rawStatus ?? b.status) !== 'cancelled') {
      modal.confirm({
        title: 'Cancel this booking?',
        content:
          'Cancelling is IRREVERSIBLE. Once this booking is cancelled its status becomes final and ' +
          'cannot be changed back to any other status, by you or by anyone else. Continue? ' +
          'Any transfer already received is not refunded automatically — handle that with the customer.',
        okText: 'Cancel booking',
        okButtonProps: { danger: true },
        cancelText: 'Keep booking',
        onOk: () =>
          saveMut.mutateAsync({ status: v.status, flightNumber: v.flightNumber?.trim() || null }),
      });
      return;
    }
    await saveMut.mutateAsync({ status: v.status, flightNumber: v.flightNumber?.trim() || null });
  });

  const submitFbo = submitGuard.wrap(async () => {
    const v = await fboForm.validateFields();
    await saveMut.mutateAsync({
      fbo: {
        departure: { name: v.depName || null, address: v.depAddress || null, phone: v.depPhone || null },
        arrival: { name: v.arrName || null, address: v.arrAddress || null, phone: v.arrPhone || null },
      },
    });
  });

  // SHAR-738 — the override is derived from the AIRCRAFT that was chosen, not
  // typed. Only what differs from the quote is stored, so a booking put back on
  // its quoted aeroplane carries no override at all rather than a copy of the
  // quote that would stop following it.
  const submitAircraft = submitGuard.wrap(async () => {
    if (!aircraftId || !chosenAircraft) {
      message.error('Select an aircraft model');
      return;
    }
    const quoted = b.quotedAircraft;
    const chosenOperator = (operatorsQ.data?.items ?? []).find((o) => o.id === operatorId);
    const diff = (value: unknown, original: unknown) =>
      (value ?? null) === (original ?? null) ? undefined : (value ?? null);
    const override = {
      operatorName: diff(
        chosenOperator?.name ?? chosenAircraft.operator?.name ?? null,
        quoted.operatorName,
      ),
      modelName: diff(chosenAircraft.modelName ?? null, quoted.modelName),
      tailNumber: diff(chosenAircraft.tailNumber ?? null, quoted.tailNumber),
      category: diff(chosenAircraft.category ?? null, quoted.category),
      seatingCapacity: diff(chosenAircraft.seatingCapacity ?? null, quoted.seatingCapacity),
    };
    const cleaned = Object.fromEntries(
      Object.entries(override).filter(([, value]) => value !== undefined),
    );
    // SHAR-766 — carry the chosen aircraft's ID as well as its particulars.
    //
    // The override held display strings only, so a booking moved to a different
    // model kept the QUOTED aircraft's photographs: the server had the new name
    // and no way to find the new pictures. The id is stored only when something
    // actually differs, so a booking put back on its quoted aeroplane still
    // carries no override at all.
    await saveMut.mutateAsync({
      aircraftOverride: Object.keys(cleaned).length
        ? { ...cleaned, aircraftId: chosenAircraft.id }
        : null,
    });
  });

  // SHAR-735 — the same rule for the itinerary: a correction is stored on the
  // booking, the quote keeps what the customer accepted, and a route put back
  // to the quoted one stores nothing.
  const submitItinerary = submitGuard.wrap(async () => {
    const v = await itineraryForm.validateFields();
    const departure = v.departureDate
      .hour(v.departureTime.hour())
      .minute(v.departureTime.minute())
      .second(0)
      .millisecond(0);
    const quoted = b.quotedItinerary;
    const originCode = v.originCode?.trim().toUpperCase() || null;
    const destCode = v.destCode?.trim().toUpperCase() || null;
    const override: Record<string, unknown> = {};
    if (originCode && originCode !== quoted.originCode) override.originCode = originCode;
    if (destCode && destCode !== quoted.destCode) override.destCode = destCode;
    // SHAR-1192 — read on the clock of the origin being saved: the booking's own,
    // or the airport just picked. Without a zone (older API) the browser's, as before.
    const tz = zoneForCode(originCode, itineraryOriginAp, [
      [b.originCode, b.departureZoned?.timezone],
      [quoted.originCode, quoted.departureZoned?.timezone],
    ]);
    const departureAt = wallToInstant(departure, tz);
    if (departureAt !== new Date(quoted.departureDateTime).toISOString()) {
      override.departureDateTime = departureAt;
    }
    await saveMut.mutateAsync({ itinerary: Object.keys(override).length ? override : null });
  });

  const rev = b.revenue;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Button icon={<ArrowLeftOutlined />} onClick={() => nav(backTo)}>
        Back to charter bookings
      </Button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Charter booking <Text code>{b.reference ?? b.id.slice(-8)}</Text>
          </Title>
          <Text type="secondary">
            {b.originCode} → {b.destCode} · {fmtZoned(b.departureZoned, dayjs(b.departureDateTime).format('DD/MM/YYYY HH:mm'))}
          </Text>
        </div>
        <Space>
          <Tag color={CHARTER_BOOKING_STATUS_COLORS[b.status]} style={{ fontSize: 14, padding: '2px 10px' }}>
            {statusLabel(CHARTER_BOOKING_STATUS_LABELS, b.status, b.statusLabel)}
          </Tag>
          {/* SHAR-124 — cancelling is not a tidy-up, so the confirmation says what
              it does and what it does NOT do. The money is deliberately left
              alone: a charter booking is already paid, and refunding it is a
              decision taken outside the app. Hidden once cancelled rather than
              disabled — there is nothing left to press. */}
          {!cancelled ? (
            <Popconfirm
              title="Cancel this charter booking?"
              description={
                <div style={{ maxWidth: 360 }}>
                  The booking and its charter request are both cancelled, and the customer is
                  told by push and by email. No money is moved: any refund is handled outside
                  the app.
                </div>
              }
              okText="Cancel booking"
              okButtonProps={{ danger: true, loading: cancelMut.isPending }}
              cancelText="Keep it"
              onConfirm={() => cancelMut.mutate()}
            >
              <Button danger>Cancel booking</Button>
            </Popconfirm>
          ) : null}
        </Space>
      </div>

      <FlightDetailsCard
        b={b}
        extra={editBtn(() => setFlightOpen(true))}
        nav={nav}
        dismissMut={dismissMut}
      />

      {/* SHAR-735 — the SAME itinerary block the ShareJet booking screen has
          (three columns: origin + departure | flight time | destination +
          arrival), rather than one "Route" line with the airport names left
          out. An admin comparing a charter with a seat booking was reading two
          different shapes for the same information. */}
      <ItineraryCard b={b} extra={editBtn(() => setItineraryOpen(true))} />

      {/* SHAR-736 — the same "Aircraft details" block the ShareJet booking screen
          has: three columns (operator | model + tail | category + seats) and the
          same labels, not "Aircraft model" / "Number of seats".
          SHAR-738 — and editable here. The values default to the quote's, and an
          edit is stored as an override ON THE BOOKING (Mikhail, 1 Sep): the quote
          stays exactly what the customer accepted, while the booking can record
          that a different tail number actually flew. */}
      <AircraftDetailsCard b={b} extra={editBtn(() => setAircraftOpen(true))} />

      <FboCard b={b} extra={editBtn(() => setFboOpen(true))} />

      {/* SHAR-122 — the manifest, with the same control as the request page. */}
      <PassengersCard b={b} onChanged={(fresh) => qc.setQueryData(['charter-booking', id], fresh)} />

      {/* SHAR-123 — revenue. */}
      {/* SHAR-812 — the Invoices block, same as the ShareJet booking page. */}
      <CharterInvoicesCard invoices={(b as any).invoices} />

      <RevenueCard rev={rev} b={b} />

      <FlightDetailsModal
        b={b}
        rev={rev}
        flightForm={flightForm}
        flightOpen={flightOpen}
        setFlightOpen={setFlightOpen}
        submitFlight={submitFlight}
        submitGuard={submitGuard}
        saveMut={saveMut}
      />

      <FboModal
        fboForm={fboForm}
        fboOpen={fboOpen}
        setFboOpen={setFboOpen}
        submitFbo={submitFbo}
        submitGuard={submitGuard}
        saveMut={saveMut}
      />

      {/* SHAR-735 — the itinerary, edited on the booking through a modal, the
          same way the ShareJet booking screen edits its own. Arrival is not
          asked for: it follows the departure by the quoted flight time, so it
          cannot be left saying something the departure contradicts. */}
      <ItineraryModal
        itineraryForm={itineraryForm}
        itineraryOpen={itineraryOpen}
        setItineraryOpen={setItineraryOpen}
        submitItinerary={submitItinerary}
        submitGuard={submitGuard}
        saveMut={saveMut}
        onOriginAirport={setItineraryOriginAp}
        departureClock={clockName(b.departureZoned?.timezone)}
      />

      {/* SHAR-738 — the aircraft details, edited on the booking. Operator, then
          that operator's aircraft, and the category follows from the aircraft:
          the flow the ShareJet booking screen uses. Free text let an admin
          invent a model, or pair a tail number with the wrong operator. */}
      <AircraftModal
        b={b}
        aircraftOpen={aircraftOpen}
        setAircraftOpen={setAircraftOpen}
        submitAircraft={submitAircraft}
        submitGuard={submitGuard}
        saveMut={saveMut}
        operatorId={operatorId}
        setOperatorId={setOperatorId}
        aircraftId={aircraftId}
        setAircraftId={setAircraftId}
        operatorsQ={operatorsQ}
        aircraftQ={aircraftQ}
        chosenAircraft={chosenAircraft}
      />
    </Space>
  );
}
